import {Chip, Icon, Link} from '@ezcater/tapas';
import {IconDefinition} from '@fortawesome/pro-regular-svg-icons';
import useTranslation from 'next-translate/useTranslation';

import {compilePath} from '@/utils';
import useNavLinkTracking from '../hooks/useNavlinkTracking';

type MenuLinkProps = React.ComponentPropsWithRef<typeof Link> & {
  chip?: React.ReactNode;
  chipAgave?: boolean;
  chipGreen?: boolean;
  chipGuava?: boolean;
  href?: string;
  icon?: IconDefinition | React.ReactNode;
  label: string;
  path?: string;
  /** Query params appended to the rendered href only — see AppBarLink / FUNC-303. */
  queryParams?: Record<string, string>;
  subText?: React.ReactNode;
};

const iconIsFontAwesome = (icon: IconDefinition | React.ReactNode): icon is IconDefinition =>
  !!icon && typeof icon === 'object' && 'icon' in icon;

const MenuLink: React.FC<MenuLinkProps> = ({
  chip,
  chipAgave = false,
  chipGreen = false,
  chipGuava = false,
  href,
  icon,
  label,
  path,
  queryParams,
  subText,
  ...props
}) => {
  const {trackLink} = useNavLinkTracking();
  const {t} = useTranslation('app-bar');

  const trackedPath = href || compilePath(path as string);
  const linkHref = !href && path && queryParams ? compilePath(path, {}, queryParams) : trackedPath;

  const chipVariant = () => {
    if (chipAgave) return 'info';
    if (chipGreen) return 'success';
    if (chipGuava) return 'danger';
    return 'default';
  };

  return (
    <Link
      className="group flex w-full justify-start no-underline"
      href={linkHref}
      onClick={() => trackLink({path: trackedPath, title: label})}
      title={t('menus.navigatesTo', {label})}
      {...props}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-1 items-center gap-4 no-underline transition-colors duration-200 group-hover:decoration-[1.5px] hover:underline">
          {icon ? (
            iconIsFontAwesome(icon) ? (
              <Icon className="flex shrink-0 text-lg" icon={icon} />
            ) : (
              <div className="flex shrink-0">{icon}</div>
            )
          ) : null}

          {subText ? (
            <span className="flex flex-col">
              <span>{label}</span>
              <span className="text-xs text-green-500 no-underline">{subText}</span>
            </span>
          ) : (
            label
          )}
        </div>

        {chip && (
          <Chip className="text-xs" variant={chipVariant()}>
            {chip}
          </Chip>
        )}
      </div>
    </Link>
  );
};

export default MenuLink;
